import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export const AccountContext = createContext();

export const AccountProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(() => {
        return fetch('/api/auth/me', { credentials: 'include' })
            .then((response) => (response.ok ? response.json() : Promise.reject(new Error('not-authenticated'))))
            .then(async (data) => {
                setUser({ id: data.id, email: data.email, displayName: data.displayName, role: data.role });
                // Si el usuario acaba de crear una cuenta despues de guardar
                // alertas anonimas, el servidor las vincula al usuario usando
                // la cookie httpOnly. Es una llamada idempotente y no afecta
                // a cuentas que no tengan alertas pendientes.
                await fetch('/api/alerts/claim-anonymous', { method: 'POST', credentials: 'include' }).catch(() => {});
            })
            .catch(() => setUser(null))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const logout = useCallback(async () => {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
        // Recarga completa en vez de solo setUser(null) -- si otra persona
        // inicia sesion despues en el mismo dispositivo/pestaña, esto
        // garantiza que no quede ningun estado de React (anuncios de
        // vendedor, panel, etc.) del usuario anterior en memoria.
        window.location.href = '/';
    }, []);

    const submitAuth = useCallback(async (endpoint, payload) => {
        const response = await fetch(endpoint, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(data.error || 'No se pudo completar la operacion');
        }
        await refresh();
        return data;
    }, [refresh]);

    const login = useCallback((email, password) => (
        submitAuth('/api/auth/login', { email, password })
    ), [submitAuth]);

    const register = useCallback((email, password, displayName) => (
        submitAuth('/api/auth/register', { email, password, displayName })
    ), [submitAuth]);

    return (
        <AccountContext.Provider value={{ user, loading, refresh, logout, login, register }}>
            {children}
        </AccountContext.Provider>
    );
};

export function useAccount() {
    return useContext(AccountContext);
}
